/**
 * Marketing deep-link intent: language, role, and CTA attribution for Learn pages.
 *
 * Persists in sessionStorage so `/auth` signup flows and GA `sign_up` events keep
 * the language page the user came from. CTA params are first-touch per tab session
 * (same pattern as campaignAttribution.js).
 */

import { normalizeRoadmapLanguageKey } from './roadmapDisplay';
import {
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_ROLE_ID_SEARCH_PARAM,
  ROADMAP_PATHS,
  LANDING_LANGUAGE_SEARCH_PARAM,
  LANDING_ROLE_SEARCH_PARAM,
  buildRoleRoadmapLearnPath,
  readLanguageParamFromSearch,
  readRoleParamFromSearch,
} from '../components/Roadmap/roadmapPaths';

const STORAGE_KEY = 'fc:landingIntent';

/** Phase 3 learn-page attribution (e.g. cta_location=learn-python). */
export const LANDING_CTA_SOURCE_PARAM = 'cta_source';
export const LANDING_CTA_LOCATION_PARAM = 'cta_location';

export { readLanguageParamFromSearch, readRoleParamFromSearch };

const safeSessionStorage = {
  get(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage.setItem(key, value);
    } catch {
      /* private mode / disabled */
    }
  },
  remove(key) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

function parseSearch(search) {
  if (!search) return new URLSearchParams();
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

function readStoredIntent() {
  try {
    const raw = safeSessionStorage.get(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredIntent(next) {
  if (!next || typeof next !== 'object' || Object.keys(next).length === 0) {
    safeSessionStorage.remove(STORAGE_KEY);
    return;
  }
  safeSessionStorage.set(STORAGE_KEY, JSON.stringify(next));
}

/**
 * Capture language / role / CTA params from the current URL into sessionStorage.
 * Call on `/home/roadmap*` and `/auth` mounts.
 * @param {string} [search] - `location.search`
 */
export function captureLandingIntent(search) {
  if (typeof window === 'undefined') return;

  const params = parseSearch(search);
  const stored = readStoredIntent() || {};
  const next = { ...stored };

  const ctaSource = params.get(LANDING_CTA_SOURCE_PARAM);
  const ctaLocation = params.get(LANDING_CTA_LOCATION_PARAM);
  if (ctaSource && !next.cta_source) next.cta_source = ctaSource;
  if (ctaLocation && !next.cta_location) next.cta_location = ctaLocation;

  const { normalized: language } = readLanguageParamFromSearch(search);
  if (language) next.language = language;

  const roleId = readRoleParamFromSearch(search);
  if (roleId) next.roleId = roleId;

  writeStoredIntent(next);
}

/** Params to attach to GA4 sign_up / signup_completed for this session. */
export function getLandingIntentAttributionParams() {
  const stored = readStoredIntent();
  if (!stored) return {};
  const out = {};
  if (stored.cta_source) out.cta_source = stored.cta_source;
  if (stored.cta_location) out.cta_location = stored.cta_location;
  if (stored.language) out.language = stored.language;
  return out;
}

/** Stored normalized language slug, if any. */
export function getStoredLandingLanguage() {
  const stored = readStoredIntent();
  const lang = stored?.language;
  return lang ? normalizeRoadmapLanguageKey(lang) : null;
}

/**
 * Post-auth destination when marketing intent was captured; null → default `/home`.
 * @returns {string|null}
 */
export function getPostAuthRedirectPath() {
  const stored = readStoredIntent();
  if (!stored) return null;

  const language = stored.language ? normalizeRoadmapLanguageKey(stored.language) : '';
  const roleId = stored.roleId ? String(stored.roleId).trim() : '';

  if (language) {
    return buildRoadmapLanguageLearnPath(language);
  }
  if (roleId) {
    return buildRoleRoadmapLearnPath(roleId);
  }
  return null;
}

export function clearLandingIntent() {
  safeSessionStorage.remove(STORAGE_KEY);
}

/**
 * Canonical Learn URL for a language deep link.
 * @param {string} languageKey
 * @returns {string}
 */
export function buildRoadmapLanguageLearnPath(languageKey) {
  const lang = normalizeRoadmapLanguageKey(languageKey);
  if (!lang) return ROADMAP_PATHS.menu;
  const params = new URLSearchParams();
  params.set(ROADMAP_GRAPH_LANG_SEARCH_PARAM, lang);
  const qs = params.toString();
  return qs ? `${ROADMAP_PATHS.menu}?${qs}` : ROADMAP_PATHS.menu;
}

/**
 * Replace marketing alias params with canonical in-app query keys.
 * @param {string} search
 * @returns {{ changed: boolean, search: string }}
 */
export function canonicalizeLearnSearchParams(search) {
  const params = parseSearch(search);
  let changed = false;

  const { raw: langRaw, normalized: langNorm } = readLanguageParamFromSearch(search);
  if (params.has(LANDING_LANGUAGE_SEARCH_PARAM)) {
    params.delete(LANDING_LANGUAGE_SEARCH_PARAM);
    changed = true;
  }
  if (langNorm) {
    if (params.get(ROADMAP_GRAPH_LANG_SEARCH_PARAM) !== langNorm) {
      params.set(ROADMAP_GRAPH_LANG_SEARCH_PARAM, langNorm);
      changed = true;
    }
  } else if (langRaw) {
    params.delete(ROADMAP_GRAPH_LANG_SEARCH_PARAM);
    changed = true;
  }

  const roleId = readRoleParamFromSearch(search);
  if (params.has(LANDING_ROLE_SEARCH_PARAM)) {
    params.delete(LANDING_ROLE_SEARCH_PARAM);
    changed = true;
  }
  if (roleId) {
    if (params.get(ROADMAP_ROLE_ID_SEARCH_PARAM) !== roleId) {
      params.set(ROADMAP_ROLE_ID_SEARCH_PARAM, roleId);
      changed = true;
    }
  }

  return { changed, search: params.toString() };
}
